import { supabase } from '@/lib/supabase';
import {
  DRAW_HISTORY_COLUMNS,
  sanitizeDraw,
  type Draw,
} from '@/lib/drawCore';

// 순수 타입/검증/정규화 헬퍼는 drawCore.ts 로 분리되어 재수출된다.
// (통계·페이지네이션 등 순수 모듈이 Supabase 를 끌어오지 않도록 하기 위함)
export {
  DRAW_HISTORY_COLUMNS,
  DRAW_PAGE_SIZE,
  GENERIC_DRAW_ERROR,
  isValidDrawNumbers,
  parseDateOnly,
  sanitizeDraw,
} from '@/lib/drawCore';
export type { Draw, DateParts } from '@/lib/drawCore';

export async function fetchDrawCount(
  signal: AbortSignal,
): Promise<{ count: number; error: boolean }> {
  const { count, error } = await supabase
    .from('draw_history')
    .select('id', { count: 'exact', head: true })
    .abortSignal(signal);
  if (error) {
    // 정상 abort는 오류로 기록하지 않음
    if (signal.aborted) return { count: 0, error: false };
    // 상세 오류는 개발 로그로만 남기고 공개 UI에는 노출하지 않음
    if (import.meta.env.DEV) console.error('[drawHistory] exact count 조회 실패:', error.message);
    return { count: 0, error: true };
  }
  return { count: count ?? 0, error: false };
}

export async function fetchDrawPage(
  from: number,
  to: number,
  signal: AbortSignal,
): Promise<{ rows: Draw[]; invalid: number; error: boolean }> {
  const { data, error } = await supabase
    .from('draw_history')
    .select(DRAW_HISTORY_COLUMNS)
    .order('id', { ascending: true })
    .range(from, to)
    .abortSignal(signal);
  if (error) {
    // 정상 abort는 오류로 기록하지 않음
    if (signal.aborted) return { rows: [], invalid: 0, error: false };
    if (import.meta.env.DEV) console.error('[drawHistory] 페이지 조회 실패:', error.message);
    return { rows: [], invalid: 0, error: true };
  }

  const rows: Draw[] = [];
  let invalid = 0;
  for (const row of (data ?? []) as Record<string, unknown>[]) {
    const sanitized = sanitizeDraw(row);
    if (sanitized) rows.push(sanitized);
    else invalid += 1;
  }
  return { rows, invalid, error: false };
}